import { Task, defModule, type TaskMessage, type UpdateResult } from '@st/redux'
import type { ConnectionSummary, SDKRequestFor } from '@st/sdk'
import { Effect } from 'effect'
import { match, P } from '@st/pattern'
import { SdkClient } from '~/effects'
import { observeOrganizationRoute } from '~/features/module-runtime'

type IntegrationContext = {
  organizationId: string
}

export type OrganizationIntegrationsState =
  | { status: 'idle' }
  | (IntegrationContext & { status: 'loading' })
  | (IntegrationContext & {
      status: 'loaded'
      connection: ConnectionSummary | null
      confirmingDisconnect: boolean
    })
  | (IntegrationContext & { status: 'disconnecting'; connection: ConnectionSummary })

const observeIntegrationsRoute = observeOrganizationRoute({
  name: 'manage_organization_integrations'
})

const loadConnection = Task.define((organizationId: string) =>
  Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* sdk.send({
      type: 'integrations/getProConnectConnection',
      organizationId
    } satisfies SDKRequestFor<'integrations/getProConnectConnection'>)

    return { type: 'connectionLoaded', connection: result.connection ?? null } as const
    // No failure has a product response yet, so an unexpected one is a defect.
  }).pipe(Effect.orDie)
)

const disconnect = Task.define((connectionId: string) =>
  Effect.gen(function* () {
    const sdk = yield* SdkClient
    yield* sdk.send({
      type: 'integrations/disconnectProConnect',
      connectionId
    } satisfies SDKRequestFor<'integrations/disconnectProConnect'>)

    return { type: 'connectionDisconnected' } as const
    // No failure has a product response yet, so an unexpected one is a defect.
  }).pipe(Effect.orDie)
)

export type OrganizationIntegrationsMessage =
  | TaskMessage<typeof observeIntegrationsRoute>
  | TaskMessage<typeof loadConnection>
  | TaskMessage<typeof disconnect>
  | { type: 'disconnectClicked' }
  | { type: 'disconnectCancelled' }
  | { type: 'disconnectRequested' }

export const organizationIntegrationsModule = defModule<{
  state: OrganizationIntegrationsState
  message: OrganizationIntegrationsMessage
  init: undefined
}>({
  init: () => ({ status: 'idle' }),
  tasks: (state) => {
    const observations = [observeIntegrationsRoute()]

    return match(state)
      .with({ status: 'loading' }, (state) => [...observations, loadConnection(state.organizationId)])
      .with({ status: 'disconnecting' }, (state) => [
        ...observations,
        disconnect(state.connection.id)
      ])
      .otherwise(() => observations)
  },
  update: updateOrganizationIntegrations
})

function updateOrganizationIntegrations(
  state: OrganizationIntegrationsState,
  message: OrganizationIntegrationsMessage
): UpdateResult<OrganizationIntegrationsState, OrganizationIntegrationsMessage> {
  return match({ state, message })
    .returnType<UpdateResult<OrganizationIntegrationsState, OrganizationIntegrationsMessage>>()
    .with({ message: { type: 'organizationRouteChanged' } }, ({ state, message }) => {
      if (message.context.status != 'loaded') return { status: 'idle' }
      if ('organizationId' in state && state.organizationId == message.context.organization.id) {
        return state
      }
      return { status: 'loading', organizationId: message.context.organization.id }
    })
    .with(
      { state: { status: 'loading' }, message: { type: 'connectionLoaded' } },
      ({ state, message }) => ({
        ...state,
        status: 'loaded',
        connection: message.connection,
        confirmingDisconnect: false
      })
    )
    .with(
      {
        state: { status: 'loaded', connection: { id: P.string } },
        message: { type: P.union('disconnectClicked', 'disconnectCancelled') }
      },
      ({ state, message }) => ({
        ...state,
        confirmingDisconnect: message.type == 'disconnectClicked'
      })
    )
    .with(
      {
        state: { status: 'loaded', connection: { id: P.string } },
        message: { type: 'disconnectRequested' }
      },
      ({ state }) => ({
        organizationId: state.organizationId,
        status: 'disconnecting',
        connection: state.connection
      })
    )
    .with(
      { state: { status: 'disconnecting' }, message: { type: 'connectionDisconnected' } },
      ({ state }) => ({
        organizationId: state.organizationId,
        status: 'loaded',
        connection: null,
        confirmingDisconnect: false
      })
    )
    .otherwise(({ state, message }) => {
      throw new Error(`Unexpected integrations message ${message.type} while ${state.status}`)
    })
}

export function selOrganizationIntegrations(state: OrganizationIntegrationsState) {
  return match(state)
    .with({ status: P.union('idle', 'loading') }, () => ({ status: 'loading' }) as const)
    .with({ status: 'loaded', connection: null }, () => ({ status: 'none' }) as const)
    .with(
      { status: P.union('loaded', 'disconnecting'), connection: P.nonNullable },
      (state) =>
        ({
          status: 'connected',
          externalAccountId: state.connection.externalAccountId,
          disconnecting: state.status == 'disconnecting',
          confirmingDisconnect: state.status == 'loaded' && state.confirmingDisconnect
        }) as const
    )
    .exhaustive()
}
